import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

/**
 * Two utilization donuts side-by-side for a plant.
 * Left  = "Where did today's consumption come from?"   → PV / Import / Discharge
 * Right = "Where did today's production go?"            → Export / Charge / Home
 *
 * Skips render entirely for on-grid plants that don't report battery data.
 */

const CONS_COLORS = { pv: "#0A0A0A", import: "#8B5CF6", discharge: "#2FA84F" };
const PROD_COLORS = { export: "#0A0A0A", charge: "#3B82F6", consumption: "#F59E0B" };

function fmtKwh(v) {
    if (v == null) return "0";
    if (v >= 100) return v.toFixed(0);
    return v.toFixed(1);
}

function Donut({ title, total, segments, colors, testid }) {
    const data = useMemo(() => segments.filter((s) => (s.value || 0) > 0), [segments]);
    const sum = data.reduce((a, b) => a + (b.value || 0), 0);
    const empty = sum <= 0;
    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-5 flex-1" data-testid={testid}>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{title}</div>
            <div className="text-sm text-zinc-700 mt-0.5 mb-3">Yesterday's split</div>
            <div className="relative h-56">
                {empty ? (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-400">
                        No data
                    </div>
                ) : (
                    <>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="value"
                                    nameKey="label"
                                    innerRadius="60%"
                                    outerRadius="90%"
                                    startAngle={90}
                                    endAngle={-270}
                                    paddingAngle={2}
                                    stroke="none"
                                >
                                    {data.map((entry, i) => (
                                        <Cell key={i} fill={colors[entry.key]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12, fontFamily: "Outfit" }}
                                    formatter={(v, n) => [`${fmtKwh(v)} kWh · ${((v / sum) * 100).toFixed(0)}%`, n]}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Centered total in the donut hole */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total</div>
                            <div className="font-mono text-2xl font-medium text-zinc-800">{fmtKwh(total)}</div>
                            <div className="text-[10px] text-zinc-500">kWh</div>
                        </div>
                    </>
                )}
            </div>
            <div className="mt-3 space-y-1.5" data-testid={`${testid}-legend`}>
                {segments.map((s) => {
                    const pct = sum > 0 ? Math.round(((s.value || 0) / sum) * 100) : 0;
                    return (
                        <div key={s.key} className="flex items-center text-xs">
                            <span className="w-2 h-2 rounded-full mr-2" style={{ background: colors[s.key] }} />
                            <span className="text-zinc-600 flex-1">{s.label}</span>
                            <span className="font-mono text-zinc-800">{fmtKwh(s.value)} kWh</span>
                            <span className="font-mono text-zinc-400 ml-2 w-8 text-right">{pct}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function UtilizationDonuts({ utilization, isHybrid }) {
    if (!utilization) {
        return (
            <div className="bg-white border border-zinc-200 rounded-sm p-5 text-xs text-zinc-500" data-testid="util-empty">
                Utilization aggregates are not available for this plant yet — waiting for at least one full day of telemetry.
            </div>
        );
    }
    const cons = utilization.consumption_breakdown || {};
    const prod = utilization.production_breakdown || {};
    const consumptionSegments = [
        { key: "pv", label: "From PV (direct)", value: cons.pv_kwh },
        { key: "import", label: "From Grid Import", value: cons.import_kwh },
        // Only show Discharge for hybrid systems — noise for on-grid.
        ...(isHybrid ? [{ key: "discharge", label: "From Battery Discharge", value: cons.discharge_kwh }] : []),
    ];
    const productionSegments = [
        { key: "export", label: "Exported to Grid", value: prod.export_kwh },
        ...(isHybrid ? [{ key: "charge", label: "To Battery Charge", value: prod.charge_kwh }] : []),
        { key: "consumption", label: "Consumed Directly", value: prod.consumption_kwh },
    ];
    return (
        <div className="flex flex-col md:flex-row gap-6" data-testid="utilization-donuts">
            <Donut
                title="Consumption Split"
                total={utilization.consumption_kwh}
                segments={consumptionSegments}
                colors={CONS_COLORS}
                testid="donut-consumption"
            />
            <Donut
                title="Production Split"
                total={utilization.production_kwh}
                segments={productionSegments}
                colors={PROD_COLORS}
                testid="donut-production"
            />
        </div>
    );
}
